import { FC, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigation } from '@utilities/hooks';
import { NavigationPathsEnum } from '@utilities/enums';
import { useAuth } from '@data/contexts';
import { verifyOtp, sendOtp } from '@services/admin.service';

const OTP_LENGTH = 6;

export const OtpVerification: FC = () => {
    const { navigateTo } = useNavigation();
    const { login } = useAuth();
    const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resendLoading, setResendLoading] = useState(false);
    const [resendMessage, setResendMessage] = useState<string | null>(null);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const email = sessionStorage.getItem('otp_email');

    useEffect(() => {
        if (!email) {
            navigateTo(NavigationPathsEnum.LOGIN);
        }
    }, [email, navigateTo]);

    const handleChange = useCallback((index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;

        setOtp(prev => {
            const newOtp = [...prev];
            newOtp[index] = value.slice(-1);
            return newOtp;
        });

        // Avancer au champ suivant
        if (value && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    }, []);

    const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    }, [otp]);

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (pastedData) {
            const newOtp = Array(OTP_LENGTH).fill('');
            pastedData.split('').forEach((char, i) => { newOtp[i] = char; });
            setOtp(newOtp);
            const focusIndex = Math.min(pastedData.length, OTP_LENGTH - 1);
            inputRefs.current[focusIndex]?.focus();
        }
    }, []);

    const handleSubmit = useCallback(async (e: FormEvent) => {
        e.preventDefault();
        if (!email) return;

        const otpCode = otp.join('');
        if (otpCode.length !== OTP_LENGTH) return;

        setError(null);
        setLoading(true);

        try {
            const response = await verifyOtp(email, otpCode);
            login(response.data.token, response.data.admin);
            sessionStorage.removeItem('otp_email');
            navigateTo(NavigationPathsEnum.DASHBOARD);
        } catch (err: any) {
            setError(err.message || 'Code invalide ou expiré');
            setOtp(Array(OTP_LENGTH).fill(''));
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    }, [email, otp, login, navigateTo]);

    const handleResend = useCallback(async () => {
        if (!email) return;
        setResendLoading(true);
        setResendMessage(null);
        setError(null);

        try {
            await sendOtp(email);
            setResendMessage('Un nouveau code a été envoyé');
        } catch (err: any) {
            setError(err.message || "Erreur lors de l'envoi");
        } finally {
            setResendLoading(false);
        }
    }, [email]);

    if (!email) return null;

    return (
        <div className="min-h-screen flex items-center justify-center bg-base-200">
            <div className="card w-full max-w-md bg-base-100 shadow-xl">
                <div className="card-body">
                    <h2 className="card-title text-2xl font-bold mb-2">Vérification</h2>
                    <p className="text-base-content/60 mb-6">
                        Entrez le code à 6 chiffres envoyé à <strong>{email}</strong>
                    </p>

                    <form onSubmit={handleSubmit}>
                        <div className="flex justify-center gap-3 mb-6">
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => { inputRefs.current[index] = el; }}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    className="input input-bordered w-12 h-14 text-center text-xl font-bold"
                                    value={digit}
                                    onChange={(e) => handleChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onPaste={index === 0 ? handlePaste : undefined}
                                    disabled={loading}
                                />
                            ))}
                        </div>

                        {error && (
                            <div className="alert alert-error mb-4">
                                <span>{error}</span>
                            </div>
                        )}

                        {resendMessage && (
                            <div className="alert alert-success mb-4">
                                <span>{resendMessage}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            className={`btn btn-primary w-full mb-3 ${loading ? 'loading' : ''}`}
                            disabled={loading || otp.join('').length !== OTP_LENGTH}
                        >
                            {loading ? 'Vérification...' : 'Vérifier'}
                        </button>

                        <div className="text-center">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={handleResend}
                                disabled={resendLoading}
                            >
                                {resendLoading ? 'Envoi...' : 'Renvoyer le code'}
                            </button>
                        </div>
                    </form>

                    <div className="mt-4 text-center">
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => {
                                sessionStorage.removeItem('otp_email');
                                navigateTo(NavigationPathsEnum.LOGIN);
                            }}
                        >
                            ← Changer d'adresse email
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
